import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  NgZone,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { SnackbarService } from '../../../services/design/snackbar.service';
import {
  DeliveryAreaPoint,
  StoreService,
} from '../../../services/stores/store.service';

const GST_NUMBER_PATTERN =
  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/i;
const PAN_NUMBER_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/i;
const FSSAI_LICENSE_PATTERN = /^[0-9]{14}$/;
const MAX_DELIVERY_AREA_POINTS = 100;

@Component({
  selector: 'app-add-store',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, GoogleMapsModule],
  templateUrl: './add-store.component.html',
})
export class AddStoreComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private storeService = inject(StoreService);
  private snackbar = inject(SnackbarService);
  private ngZone = inject(NgZone);

  isEditMode = false;
  isSubmitting = false;
  storeId?: number;
  mapsReady = signal(false);
  placesReady = signal(false);

  readonly locationSearchInput =
    viewChild<ElementRef<HTMLInputElement>>('locationSearch');

  markerPosition = signal<google.maps.LatLngLiteral | null>(null);
  deliveryArea = signal<DeliveryAreaPoint[]>([]);
  isDrawingDeliveryArea = signal(false);
  center = signal<google.maps.LatLngLiteral>({
    lat: 26.7606,
    lng: 83.3732,
  });

  storeForm = this.fb.nonNullable.group({
    name: ['', Validators.required],
    latitude: [0, Validators.required],
    longitude: [0, Validators.required],
    is_active: [true],
    store_code: ['', Validators.required],
    address: ['', Validators.required],
    city: ['', Validators.required],
    store_radius: [0],
    opening_time: ['08:00'],
    closing_time: ['22:00'],
    contact_number: [''],
    email: ['', Validators.email],
    legal_business_name: [''],
    gst_number: ['', [Validators.pattern(GST_NUMBER_PATTERN)]],
    pan_number: ['', [Validators.pattern(PAN_NUMBER_PATTERN)]],
    fssai_license_number: ['', [Validators.pattern(FSSAI_LICENSE_PATTERN)]],
  });

  readonly defaultCenter: google.maps.LatLngLiteral = {
    lat: 26.7606,
    lng: 83.3732,
  };

  ngOnInit() {
    this.loadGoogleMaps();
    this.checkMapsReady();

    // Retry check if not ready yet (e.g. script still loading)
    if (!this.mapsReady()) {
      const interval = setInterval(() => {
        if (this.checkMapsReady()) {
          clearInterval(interval);
        }
      }, 500);
      // Auto-clear after 10 seconds to avoid infinite loops
      setTimeout(() => clearInterval(interval), 10000);
    }

    this.markerPosition.set(this.center());

    // Auto-generate store code logic
    this.storeForm.controls.name.valueChanges.subscribe(() =>
      this.generateStoreCode()
    );
    this.storeForm.controls.city.valueChanges.subscribe(() =>
      this.generateStoreCode()
    );
    // Keep marker in sync when lat/lng inputs change
    this.storeForm.controls.latitude.valueChanges.subscribe(() => {
      if (!this.mapsReady()) return;
      this.updateMarkerFromForm();
    });
    this.storeForm.controls.longitude.valueChanges.subscribe(() => {
      if (!this.mapsReady()) return;
      this.updateMarkerFromForm();
    });

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditMode = true;
      this.storeId = +id;
      this.loadStore(this.storeId);
    }
  }

  async loadStore(id: number) {
    try {
      const store = await firstValueFrom(this.storeService.getSingleStore(id));

      this.storeForm.patchValue({
        name: store.name,
        address: store.address,
        longitude: store.longitude ?? undefined,
        latitude: store.latitude ?? undefined,
        store_code: store.store_code,
        store_radius: store.store_radius,
        city: store.city,
        is_active: store.is_active,
        opening_time: store.opening_time || '08:00',
        closing_time: store.closing_time || '22:00',
        contact_number: store.contact_number || '',
        email: store.email || '',
        legal_business_name: store.legal_business_name || '',
        gst_number: store.gst_number || '',
        pan_number: store.pan_number || '',
        fssai_license_number: store.fssai_license_number || '',
      });

      const newPos = {
        lat: store.latitude || this.defaultCenter.lat,
        lng: store.longitude || this.defaultCenter.lng,
      };
      this.markerPosition.set(newPos);
      this.center.set(newPos);
      this.deliveryArea.set(this.normalizeDeliveryArea(store.delivery_area));
    } catch (err) {
      console.error('Failed to load store', err);
    }
  }

  async submit() {
    if (this.storeForm.invalid) {
      this.storeForm.markAllAsTouched();
      this.snackbar.error('Please fix validation errors');
      return;
    }

    const deliveryArea = this.deliveryArea();
    const hasCustomDeliveryArea = deliveryArea.length >= 3;
    const hasLegacyRadius =
      this.isEditMode && Number(this.storeForm.controls.store_radius.value) > 0;

    if (!hasCustomDeliveryArea && !hasLegacyRadius) {
      this.snackbar.error('Draw a delivery area with at least 3 boundary points');
      return;
    }

    this.isSubmitting = true;
    const formValue = this.storeForm.getRawValue();
    const payload: any = {
      name: formValue.name.trim(),
      latitude: formValue.latitude,
      longitude: formValue.longitude,
      is_active: formValue.is_active,
      store_code: formValue.store_code.trim().toUpperCase(),
      address: formValue.address.trim(),
      city: formValue.city.trim(),
      store_radius: formValue.store_radius,
      delivery_area: hasCustomDeliveryArea ? deliveryArea : null,
      opening_time: formValue.opening_time,
      closing_time: formValue.closing_time,
      contact_number: formValue.contact_number.trim() || null,
      email: formValue.email.trim() || null,
      legal_business_name: formValue.legal_business_name.trim() || null,
      gst_number: this.normalizeIdentifier(formValue.gst_number) || null,
      pan_number: this.normalizeIdentifier(formValue.pan_number) || null,
      fssai_license_number: formValue.fssai_license_number.trim() || null,
      updated_at: new Date().toISOString(),
    };

    try {
      if (this.isEditMode && this.storeId) {
        await this.storeService.updateStore(this.storeId, payload);
        this.snackbar.success('Store information updated');
      } else {
        await this.storeService.createStore(payload);
        this.snackbar.success('Store created successfully');
      }

      this.router.navigate(['/stores']);
    } catch (err) {
      console.error(err);
      this.snackbar.error('Failed to save store');
    } finally {
      this.isSubmitting = false;
    }
  }

  onMapClick(event: any) {
    if (!event?.latLng) return;
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    if (this.isDrawingDeliveryArea()) {
      if (this.deliveryArea().length >= MAX_DELIVERY_AREA_POINTS) {
        this.snackbar.warning(
          `A delivery area can have up to ${MAX_DELIVERY_AREA_POINTS} points`
        );
        return;
      }

      this.deliveryArea.update((points) => [...points, { lat, lng }]);
      return;
    }

    this.storeForm.patchValue({
      latitude: lat,
      longitude: lng,
    });
    this.markerPosition.set({ lat, lng });
    this.reverseGeocode(lat, lng);
  }

  onMarkerDragEnd(event: any) {
    if (!event?.latLng) return;
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    this.storeForm.patchValue({
      latitude: lat,
      longitude: lng,
    });
    this.markerPosition.set({ lat, lng });
    this.reverseGeocode(lat, lng);
  }

  private reverseGeocode(lat: number, lng: number): void {
    if (!this.mapsReady()) return;

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      this.ngZone.run(() => {
        if (status === 'OK' && results && results[0]) {
          const place = results[0];

          // Extract city (locality)
          const cityComp = place.address_components.find((c) =>
            c.types.includes('locality')
          );

          this.storeForm.patchValue({
            address: place.formatted_address,
            city: cityComp?.long_name || '',
          });
        }
      });
    });
  }

  private updateMarkerFromForm() {
    const lat = Number(this.storeForm.controls.latitude.value);
    const lng = Number(this.storeForm.controls.longitude.value);
    if (
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      (lat !== 0 || lng !== 0)
    ) {
      this.markerPosition.set({ lat, lng });
    }
  }

  startDeliveryAreaDrawing(): void {
    this.isDrawingDeliveryArea.set(true);
  }

  finishDeliveryAreaDrawing(): void {
    if (this.deliveryArea().length < 3) {
      this.snackbar.warning('Add at least 3 boundary points');
      return;
    }

    this.isDrawingDeliveryArea.set(false);
  }

  undoDeliveryAreaPoint(): void {
    this.deliveryArea.update((points) => points.slice(0, -1));
  }

  clearDeliveryArea(): void {
    this.deliveryArea.set([]);
    this.isDrawingDeliveryArea.set(true);
  }

  // Called from template button (keeps things simple vs relying on view init order)
  enablePlacesSearch(): void {
    if (!this.mapsReady() || !this.placesReady()) {
      this.snackbar.warning('Google Places library is not available');
      return;
    }

    const input = this.locationSearchInput()?.nativeElement;
    if (!input) return;

    // Avoid double init
    if ((input as any).__places_inited) return;
    (input as any).__places_inited = true;

    const googleAny = (globalThis as any).google as typeof google | undefined;
    if (!googleAny?.maps?.places) return;

    const autocomplete = new googleAny.maps.places.Autocomplete(input, {
      fields: ['geometry', 'formatted_address', 'name'],
      types: ['geocode'],
    });

    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = autocomplete.getPlace();
        const loc = place?.geometry?.location;
        if (!loc) return;
        const newPos = { lat: loc.lat(), lng: loc.lng() };
        this.storeForm.patchValue({
          latitude: newPos.lat,
          longitude: newPos.lng,
        });
        this.markerPosition.set(newPos);
        this.center.set(newPos);
      });
    });
  }

  cancel() {
    this.router.navigate(['/stores']);
  }

  reset(): void {
    if (this.isSubmitting || this.isEditMode) return;
    this.storeForm.reset({
      name: '',
      latitude: 0,
      longitude: 0,
      is_active: true,
      store_code: '',
      address: '',
      city: '',
      store_radius: 0,
      opening_time: '08:00',
      closing_time: '22:00',
      contact_number: '',
      email: '',
      legal_business_name: '',
      gst_number: '',
      pan_number: '',
      fssai_license_number: '',
    });
    this.deliveryArea.set([]);
    this.isDrawingDeliveryArea.set(false);
  }

  private checkMapsReady(): boolean {
    const isMapsReady = !!(globalThis as any)?.google?.maps;
    const isPlacesReady = !!(globalThis as any)?.google?.maps?.places;
    this.mapsReady.set(isMapsReady);
    this.placesReady.set(isPlacesReady);
    return isMapsReady && isPlacesReady;
  }

  private loadGoogleMaps(): void {
    if ((globalThis as any).google?.maps) return;

    const apiKey = environment.googleMapsApiKey;
    const scriptId = 'google-maps-script';

    if (document.getElementById(scriptId)) return;

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  private generateStoreCode(): void {
    if (this.isEditMode || this.isSubmitting) return;

    const name = this.storeForm.controls.name.value;
    const city = this.storeForm.controls.city.value;

    if (!name) {
      this.storeForm.controls.store_code.setValue('');
      return;
    }

    const cityPart = city ? city.substring(0, 3).toUpperCase() : 'STR';

    const nameWords = name.trim().split(/\s+/);
    const namePart = nameWords
      .map((word: string) => word[0])
      .join('')
      .substring(0, 4)
      .toUpperCase();

    const timestamp = new Date().getTime().toString().slice(-3);
    const code = `${cityPart}-${namePart}-${timestamp}`;

    const currentCode = this.storeForm.controls.store_code.value;
    if (!currentCode || currentCode.includes('-')) {
      this.storeForm.controls.store_code.setValue(code);
    }
  }

  private normalizeIdentifier(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toUpperCase();
  }

  private normalizeDeliveryArea(value: unknown): DeliveryAreaPoint[] {
    if (!Array.isArray(value)) return [];

    return value
      .map((point) => ({
        lat: Number((point as DeliveryAreaPoint)?.lat),
        lng: Number((point as DeliveryAreaPoint)?.lng),
      }))
      .filter(
        (point) =>
          Number.isFinite(point.lat) &&
          Number.isFinite(point.lng) &&
          Math.abs(point.lat) <= 90 &&
          Math.abs(point.lng) <= 180
      )
      .slice(0, MAX_DELIVERY_AREA_POINTS);
  }

  get f() {
    return this.storeForm.controls;
  }
}
